import type { LeadPayload, QuizDefinition } from "@quiz/quiz-core";
import { getQuizPresentation } from "@quiz/quiz-definitions";
import { ArrowRight, createElement } from "lucide";
import { PhoneInput } from "../components/PhoneInput";
import { submitLead, type LeadResponse } from "../api/leadApi";
import { button, el } from "../utils";
import type { ResolvedUiCopy } from "../ui/copy";
import { renderCampaignMedia } from "../components/CampaignMedia";

export interface LeadFormOptions {
  quiz: QuizDefinition;
  copy: ResolvedUiCopy;
  createPayload: (contact: LeadPayload["lead"], consent: LeadPayload["consent"]) => LeadPayload;
  onSubmit?: (payload: LeadPayload) => void;
  onStart?: (fieldName: string) => void;
  onValidationError?: (fieldName: string, errorCode: string) => void;
  onSuccess: (response: LeadResponse, payload: LeadPayload) => void;
  onError: (message: string) => void;
}

export function renderLeadScreen(options: LeadFormOptions): HTMLElement {
  if (getQuizPresentation(options.quiz).comicQuest) return renderComicQuestLeadScreen(options);
  const screen = el("main", "screen lead-screen");
  const shell = el("section", "lead-shell");
  shell.append(el("h1", "lead-title", options.quiz.leadForm.title));
  if (options.quiz.leadForm.subtitle) shell.append(el("p", "lead-subtitle", options.quiz.leadForm.subtitle));
  shell.append(renderLeadForm(options));
  screen.append(shell);
  return screen;
}

function renderComicQuestLeadScreen(options: LeadFormOptions): HTMLElement {
  const comic = getQuizPresentation(options.quiz).comicQuest!;
  const screen = el("main", "screen lead-screen comic-lead-screen");
  if (comic.leadArt && comic.leadArt.enabled !== false) {
    const backdrop = comic.leadArt.type === "image" ? comic.leadArt.src : comic.leadArt.reducedMotionSrc;
    const mobileBackdrop = comic.leadArt.type === "image" ? comic.leadArt.mobileSrc ?? backdrop : backdrop;
    screen.style.setProperty("--comic-lead-backdrop", `url("${backdrop}")`);
    screen.style.setProperty("--comic-lead-mobile-backdrop", `url("${mobileBackdrop}")`);
  }
  const shell = el("section", "comic-lead");
  const visual = el("div", "comic-lead__visual");
  const art = renderCampaignMedia(comic.leadArt, "comic-lead__art");
  if (art) visual.append(art);

  const brand = el("header", "comic-result__brand comic-lead__brand");
  const logo = el("img", "comic-result__logo") as HTMLImageElement;
  logo.src = comic.logoSrc;
  logo.alt = options.quiz.metadata.name;
  brand.append(logo, el("span", "comic-result__chapter", comic.copy?.leadChapter ?? "Лист із Блеквуда"));
  visual.append(brand);

  const panel = el("section", "comic-lead__panel");
  panel.append(el("p", "comic-result__eyebrow", "Ще один крок"));
  panel.append(el("h1", "lead-title", options.quiz.leadForm.title));
  if (options.quiz.leadForm.subtitle) panel.append(el("p", "lead-subtitle", options.quiz.leadForm.subtitle));
  panel.append(renderLeadForm(options));

  shell.append(visual, panel);
  screen.append(shell);
  return screen;
}

export function renderLeadForm(options: LeadFormOptions): HTMLFormElement {
  const form = el("form", "lead-form") as HTMLFormElement;
  const compact = getQuizPresentation(options.quiz).flowLayout === "compact";
  const name = inputField("text", options.quiz.leadForm.fields.name.label ?? "Name", "name");
  const email = inputField("email", options.quiz.leadForm.fields.email.label ?? "Email", "email");
  const phone = new PhoneInput(options.quiz.phone, { search: options.copy.phoneSearch, aria: options.copy.phoneAria, locale: options.quiz.metadata.language, placeholder: compact ? options.quiz.leadForm.fields.phone.label : undefined });
  const phoneWrap = fieldWrap(options.quiz.leadForm.fields.phone.label ?? "Phone", phone.root);
  const privacy = checkbox("privacy", options.copy.privacyConsent, !getQuizPresentation(options.quiz).booking);
  const marketing = checkbox("marketing", options.copy.marketingConsent, false);
  const error = el("p", "form-error");
  error.setAttribute("role", "alert");
  error.hidden = true;
  const submit = button("primary-button lead-submit", options.quiz.leadForm.submitLabel, () => undefined);
  submit.type = "submit";
  const resetSubmit = () => {
    submit.textContent = options.quiz.leadForm.submitLabel;
    if (compact || getQuizPresentation(options.quiz).comicQuest?.coverCopy) submit.append(createElement(ArrowRight, { width: 18, height: 18, "aria-hidden": "true" }));
  };
  resetSubmit();

  if (options.quiz.leadForm.fields.name.enabled) form.append(name.wrap);
  if (options.quiz.leadForm.fields.email.enabled) form.append(email.wrap);
  if (options.quiz.leadForm.fields.phone.enabled) form.append(phoneWrap);
  if (options.quiz.leadForm.privacyConsentRequired) form.append(privacy.wrap);
  if (options.quiz.leadForm.marketingConsentEnabled) form.append(marketing.wrap);
  form.append(error);
  if (getQuizPresentation(options.quiz).comicQuest) {
    const actions = el("div", "comic-lead__actions");
    actions.append(submit);
    form.append(actions);
  } else form.append(submit);

  form.addEventListener("focusin", (event) => {
    const field = event.target instanceof HTMLInputElement ? event.target.name : "form";
    options.onStart?.(field || "form");
  }, { once: true });

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    error.hidden = true;
    phone.setInvalid(false);
    const contact: LeadPayload["lead"] = {};
    if (options.quiz.leadForm.fields.name.enabled) contact.name = name.input.value.trim();
    if (options.quiz.leadForm.fields.email.enabled) contact.email = email.input.value.trim();
    if (options.quiz.leadForm.fields.phone.enabled) {
      const normalizedPhone = phone.value;
      if (!normalizedPhone.isValid) {
        phone.setInvalid(true);
        showError(error, options.copy.invalidPhone);
        phone.focus();
        options.onValidationError?.("phone", "invalid_phone");
        return;
      }
      contact.phone = normalizedPhone;
    }
    if (options.quiz.leadForm.fields.name.required && !contact.name) {
      showError(error, options.copy.nameRequired);
      name.input.focus();
      options.onValidationError?.("name", "required");
      return;
    }
    if (options.quiz.leadForm.fields.email.required && !contact.email) {
      showError(error, options.copy.emailRequired);
      email.input.focus();
      options.onValidationError?.("email", "required");
      return;
    }
    if (options.quiz.leadForm.privacyConsentRequired && !privacy.input.checked) {
      showError(error, options.copy.privacyRequired);
      privacy.input.focus();
      options.onValidationError?.("privacy", "required");
      return;
    }

    submit.disabled = true;
    submit.textContent = options.copy.sending;
    const payload = options.createPayload(contact, {
      privacy: privacy.input.checked,
      marketing: marketing.input.checked
    });
    options.onSubmit?.(payload);
    try {
      const response = await submitLead(payload, options.copy.submitError);
      options.onSuccess(response, payload);
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : options.copy.submitError;
      showError(error, message);
      options.onError(message);
      submit.disabled = false;
      resetSubmit();
    }
  });

  return form;
}

function inputField(type: string, label: string, name: string): { wrap: HTMLElement; input: HTMLInputElement } {
  const input = el("input", "text-input") as HTMLInputElement;
  input.type = type;
  input.name = name;
  input.id = `lead-${name}`;
  input.setAttribute("autocomplete", name);
  input.placeholder = label;
  const wrap = fieldWrap(label, input);
  return { wrap, input };
}

function fieldWrap(labelText: string, control: HTMLElement): HTMLElement {
  const wrap = el("div", "field-label");
  const label = el("label", "", labelText) as HTMLLabelElement;
  const input = control instanceof HTMLInputElement ? control : control.querySelector("input");
  if (input) {
    if (!input.id) input.id = "lead-phone";
    label.htmlFor = input.id;
  }
  wrap.append(label, control);
  return wrap;
}

function checkbox(name: string, labelText: string, checked: boolean): { wrap: HTMLElement; input: HTMLInputElement } {
  const input = el("input") as HTMLInputElement;
  input.type = "checkbox";
  input.name = name;
  input.checked = checked;
  const label = el("label", "checkbox-label");
  label.append(input, el("span", "", labelText));
  return { wrap: label, input };
}

function showError(error: HTMLElement, message: string): void {
  error.hidden = false;
  error.textContent = message;
}
